import { useMemo, useState, useEffect } from 'react';
import type { ScheduledCoupon } from '@/types/collection.types';
import { useScheduledCoupons } from '@/features/collection/hooks/useScheduledCoupons';
import { getDaysUntilDue } from '@/features/collection/utils/couponUrgency';
import { AgendadosHeader, type RangeFilter } from './components/AgendadosHeader';
import { AgendadosTable, type SortState } from './components/AgendadosTable';

const PAGE_SIZE = 30;
const PARTNER_ID = 'UNE';

export function AgendadosPage() {
    const { coupons: allCoupons, isLoading, error, refetch } = useScheduledCoupons(PARTNER_ID);
    const today = new Date().toISOString().split('T')[0] as string;

    const [activeRange, setActiveRange] = useState<RangeFilter>('all');
    const [sort, setSort] = useState<SortState>({ column: 'due_date', direction: 'asc' });
    const [page, setPage] = useState(1);

    useEffect(() => { setPage(1); }, [activeRange, sort]);

    const couponsInRange = useMemo(() => {
        if (activeRange === 'all') return allCoupons;
        const limit = activeRange === '7d' ? 7 : 30;
        return allCoupons.filter(c => {
            const d = getDaysUntilDue(c.due_date, today);
            return d > 0 && d <= limit;
        });
    }, [allCoupons, activeRange, today]);

    const couponsSorted = useMemo(() => {
        if (!sort.direction) return couponsInRange;
        const dir = sort.direction === 'asc' ? 1 : -1;
        const arr = [...couponsInRange];
        arr.sort((a, b) => {
            const primary = sortValue(a, sort.column, today) < sortValue(b, sort.column, today) ? -1 :
                            sortValue(a, sort.column, today) > sortValue(b, sort.column, today) ? 1 : 0;
            if (primary !== 0) return primary * dir;
            return a.due_date.localeCompare(b.due_date);
        });
        return arr;
    }, [couponsInRange, sort, today]);

    const totalPages = Math.max(1, Math.ceil(couponsSorted.length / PAGE_SIZE));
    const clampedPage = Math.min(page, totalPages);
    const couponsPaged = couponsSorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

    if (error) {
        return (
            <div className="p-6">
                <div className="rounded border border-red-500/40 bg-red-500/10 p-4">
                    <div className="text-sm text-red-300">No pudimos cargar los cupones agendados.</div>
                    <button
                        onClick={() => refetch()}
                        className="mt-3 rounded border border-red-500/60 px-3 py-1 text-sm text-red-200 hover:bg-red-500/20"
                    >Reintentar</button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <PageTitle />
                <div className="mt-4 flex gap-3">
                    {[0, 1, 2].map(i => <div key={i} className="h-24 min-w-[140px] flex-1 animate-pulse rounded bg-slate-800" />)}
                </div>
                <div className="mt-4 space-y-2">
                    {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-slate-800/60" />)}
                </div>
            </div>
        );
    }

    if (allCoupons.length === 0) {
        return (
            <div className="p-6">
                <PageTitle />
                <div className="mt-8 rounded border border-slate-700 bg-slate-900 p-8 text-center">
                    <div className="text-slate-200">No hay cupones agendados.</div>
                    <div className="mt-1 text-sm text-slate-400">Cuando haya créditos con próximos vencimientos, van a aparecer acá.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <PageTitle />
            <div className="mt-4">
                <AgendadosHeader
                    allCoupons={allCoupons}
                    today={today}
                    activeRange={activeRange}
                    onRangeChange={setActiveRange}
                    page={clampedPage}
                    totalPages={totalPages}
                    totalCouponsInRange={couponsSorted.length}
                    onPageChange={setPage}
                />
            </div>
            <div className="mt-4">
                {couponsInRange.length === 0 ? (
                    <div className="rounded border border-slate-700 bg-slate-900 p-8 text-center">
                        <div className="text-sm text-slate-300">No hay cupones en este rango.</div>
                        <button
                            onClick={() => setActiveRange('all')}
                            className="mt-3 rounded border border-slate-600 px-3 py-1 text-sm text-slate-200 hover:bg-slate-800"
                        >Ver todo futuro</button>
                    </div>
                ) : (
                    <AgendadosTable coupons={couponsPaged} today={today} sort={sort} onSortChange={setSort} />
                )}
            </div>
        </div>
    );
}

function PageTitle() {
    return (
        <div>
            <h1 className="text-2xl font-semibold text-slate-100">Agendados</h1>
            <p className="mt-1 text-sm text-slate-400">Cupones por cobrar próximos</p>
        </div>
    );
}

function sortValue(c: ScheduledCoupon, column: SortState['column'], today: string): string | number {
    switch (column) {
        case 'display_name': return c.client.display_name.toLowerCase();
        case 'credit_id': return c.credit_id;
        case 'coupon_number': return c.coupon_number;
        case 'pending_amount_mxn': return c.pending_amount_mxn;
        case 'due_date': return c.due_date;
        case 'days': return getDaysUntilDue(c.due_date, today);
    }
}
